// src/Admin/EmailStudio/EmailStudio.js
/* =========================================================
   Email Studio (/Admin/sendemails) — the new Send Emails page.
   One screen, three columns:
     1) Audience  — who gets it, with real filters (free trial result, attended…)
     2) Message   — template + text fields
     3) Preview   — live, desktop / phone, as one real recipient
   Plus a "Sent" tab with every campaign and its report.

   Sending uses the existing endpoints (batches of 10, same as before):
     POST /api/admin/students/send-broadcast-email
     POST /api/admin/students/centers/send-broadcast
   ========================================================= */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faUserCheck,
  faUserClock,
  faHourglassHalf,
  faUserGraduate,
  faUsers,
  faBuilding,
  faFolderOpen,
  faMagnifyingGlass,
  faDesktop,
  faMobileScreen,
  faFlask,
  faTriangleExclamation,
  faCircleCheck,
  faXmark,
  faRotateRight,
  faClockRotateLeft,
  faPenNib,
  faFloppyDisk,
  faWandMagicSparkles,
  faChevronRight,
  faGift,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { fromServer, DISPLAY_TZ } from '../Servertime';
import { THEMES, LINKS, TAGS, TEMPLATES, buildEmail, fillTags, firstNameOf, leftovers } from './emailKit';
import './EmailStudio.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, ...extra } });
const SENDER = { 'X-User-Id': 'admin' };

const BATCH = 10;
const BATCH_BYTES = 80000; // the server refuses a request over 100 KB: long emails go in smaller batches
const BATCH_GAP = 5000;
const ERROR_GAP = 10000;
const DRAFT_KEY = 'rm-email-studio-draft';
const TEST_KEY = 'rm-test-email';
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
/* Up to 10 people per request, fewer when the email is long (each person carries a full copy) */
const packBatches = (list, extra) => {
  const base = JSON.stringify(extra).length;
  const out = [];
  let cur = [];
  let size = base;
  list.forEach((p) => {
    const n = JSON.stringify(p).length + 1;
    if (cur.length && (cur.length >= BATCH || size + n > BATCH_BYTES)) {
      out.push(cur);
      cur = [];
      size = base;
    }
    cur.push(p);
    size += n;
  });
  if (cur.length) out.push(cur);
  return out;
};
const sendError = (err) =>
  err?.response?.status === 413
    ? 'The email is too long for the server.'
    : err?.response?.data?.error || (err?.code === 'ECONNABORTED' ? 'The server took too long to answer.' : err?.message || 'Unknown error');
const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const store = {
  get: (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* private mode */
    }
  },
};

/* ---------- audiences ---------- */
const SOURCES = [
  { key: 'trial', label: 'Free trial families', icon: faUserCheck },
  { key: 'current', label: 'Current students', icon: faUserGraduate, query: 'studentType=active' },
  { key: 'former', label: 'Former students', icon: faUsers, query: 'studentType=enrolled&filterType=all&enrollmentFilter=all' },
  { key: 'allpaid', label: 'All paid students', icon: faUserGraduate, query: 'studentType=allpaid' },
  { key: 'centers', label: 'Centers & schools', icon: faBuilding },
  { key: 'saved', label: 'Saved lists', icon: faFolderOpen },
];

// demo report result (SubmitDemoClasses.IdClassResults)
const RESULTS = [
  { key: 'none', label: 'No report yet', test: (r) => r === null || r === undefined },
  { key: 'awaiting', label: 'Awaiting', test: (r) => r === 0 },
  { key: 'pending', label: 'Pending', test: (r) => r === 2 },
  { key: 'postponed', label: 'Postponed', test: (r) => r === 4 },
  { key: 'notinterested', label: 'Not interested', test: (r) => r === 5 },
  { key: 'closed', label: 'Closed', test: (r) => r === 3 || r === 6 },
  { key: 'enrolled', label: 'Enrolled', test: (r) => r === 1 },
];
const resultKey = (r) => (RESULTS.find((x) => x.test(r)) || RESULTS[0]).key;
const resultLabel = (r) => (RESULTS.find((x) => x.test(r)) || RESULTS[0]).label;
const ALL_RESULTS = RESULTS.map((r) => r.key);
const NOT_ENROLLED = ALL_RESULTS.filter((k) => k !== 'enrolled');

const PERIODS = [
  ['30', 'Last 30 days'],
  ['90', 'Last 3 months'],
  ['365', 'Last year'],
  ['all', 'Any time'],
];

const DEFAULT_TRIAL = { attended: 'yes', results: NOT_ENROLLED, excludePaid: true, period: 'all' };

const QUICK = [
  { key: 'q-not-enrolled', label: 'Tried a free class · not enrolled', icon: faUserCheck, source: 'trial', trial: DEFAULT_TRIAL, template: 'trial-next-step' },
  { key: 'q-missed', label: 'Missed the free class', icon: faUserClock, source: 'trial', trial: { attended: 'no', results: ALL_RESULTS.filter((k) => k !== 'enrolled'), excludePaid: true, period: 'all' }, template: 'trial-missed' },
  { key: 'q-thinking', label: 'Still deciding (pending / postponed)', icon: faHourglassHalf, source: 'trial', trial: { attended: 'any', results: ['pending', 'postponed'], excludePaid: true, period: 'all' }, template: 'trial-next-step' },
  { key: 'q-former', label: 'Former students', icon: faUsers, source: 'former', template: 'come-back' },
  { key: 'q-referral', label: 'Invite a friend (referral)', icon: faGift, source: 'allpaid', template: 'referral' },
  { key: 'q-competition', label: 'Competition', icon: faTrophy, source: 'allpaid', template: 'competition' },
];

const dateLabel = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }) : '';
};
const longDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }) : '';
};
const sentAt = (v) => {
  const d = fromServer(v);
  return d ? d.toLocaleString('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
};

const toPerson = (s, extra = {}) => ({
  id: `s-${s.id}`,
  rawId: s.id,
  kind: 'student',
  name: s.studentName || 'Student',
  email: String(s.email || '').trim(),
  lastClassDate: s.lastClassDate,
  age: s.age,
  ...extra,
});
const valuesFor = (p) =>
  p.kind === 'center'
    ? { centerName: p.name }
    : { firstName: firstNameOf(p.name), studentName: p.name, lastClassDate: longDate(p.lastClassDate), referralCode: p.referralCode };

/* campaigns: old ones were saved one row per batch of 10, so join rows with the same subject sent close together */
const groupCampaigns = (logs) => {
  const out = [];
  logs
    .filter((l) => l.Type !== 'test')
    .forEach((l) => {
      const prev = out[out.length - 1];
      const t = fromServer(l.SentAt)?.getTime() || 0;
      if (prev && prev.subject === l.Subject && prev.type === l.Type && Math.abs(prev.lastTime - t) < 3600000) {
        prev.ids.push(l.Id);
        prev.total += Number(l.RecipientCount || 0);
        prev.sent += Number(l.SuccessCount || 0);
        prev.failed += Number(l.FailedCount || 0);
        prev.lastTime = t;
        return;
      }
      out.push({ key: l.Id, ids: [l.Id], subject: l.Subject, type: l.Type, at: l.SentAt, lastTime: t, total: Number(l.RecipientCount || 0), sent: Number(l.SuccessCount || 0), failed: Number(l.FailedCount || 0) });
    });
  return out;
};

const loadDraft = () => {
  try {
    const d = JSON.parse(store.get(DRAFT_KEY) || 'null');
    return d && typeof d === 'object' ? d : null;
  } catch {
    return null;
  }
};
const fromTemplate = (key) => {
  const t = TEMPLATES.find((x) => x.key === key) || TEMPLATES[0];
  const { name, fits, ...fields } = t;
  return { ...fields, templateKey: t.key };
};

/* Email for one competition (opened from Admin > Competitions) */
const COMP_WHO = { all: 'All students', little: 'Ages 6 to 8', junior: 'Ages 9 and up' };
const compDay = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
};
const competitionDraft = (c) => {
  const base = fromTemplate('competition');
  const who = COMP_WHO[c.level] || COMP_WHO.all;
  const end = compDay(c.endDate);
  return {
    ...base,
    subject: `New RoboMind challenge: ${c.title} 🏆`,
    preheader: `${who}. Send your project before ${end}.`,
    headline: c.title,
    body: `${c.description ? `${c.description}\n\n` : ''}Send the project link from the **Competitions** page on {{firstName}}’s dashboard before **${end}**. Our teachers score every project, and the winners are announced to all students.`,
    highlights: [`Open to: ${who}`, `Last day: ${end}`, c.prize ? `Prize: ${c.prize}` : ''].filter(Boolean).join('\n'),
  };
};
// a Little / Junior challenge only goes to parents of children that age
const levelFits = (level, age) => {
  if (!level || level === 'all') return true;
  const a = Number(age);
  if (!a) return true; // age unknown: keep them
  return level === 'little' ? a < 9 : a >= 9;
};

/* =========================================================
   Page
   ========================================================= */
const EmailStudio = () => {
  const [view, setView] = useState('compose');
  const [toast, setToast] = useState(null);
  const say = (text, tone = 'good') => setToast({ text, tone, id: Date.now() });
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  /* ----- audience ----- */
  const [source, setSource] = useState('trial');
  const [quick, setQuick] = useState('q-not-enrolled');
  const [trial, setTrial] = useState(DEFAULT_TRIAL);
  const [data, setData] = useState({}); // source -> list | null (loading) | 'error'
  const [groups, setGroups] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [compLevel, setCompLevel] = useState(null); // competition email: 'little' / 'junior' limits "All paid students"

  const loadSource = useCallback(async (key) => {
    setData((d) => ({ ...d, [key]: null }));
    try {
      let list = [];
      if (key === 'trial') {
        const res = await axios.get(`${API}/api/admin/students/email-audience/demo`, auth());
        list = (res.data?.students || []).map((s) =>
          toPerson(s, { attended: s.attended === 1 || s.attended === true, result: s.result, demoDate: s.demoDate, paidAccount: s.paidAccount === 1 || s.paidAccount === true })
        );
      } else if (key === 'centers') {
        const res = await axios.get(`${API}/api/admin/students/centers`, auth());
        list = (res.data?.centers || []).map((c) => ({ id: `c-${c.Id}`, rawId: c.Id, kind: 'center', name: c.CenterName, email: String(c.Email || '').trim() }));
      } else {
        const src = SOURCES.find((s) => s.key === key);
        const res = await axios.get(`${API}/api/admin/students/all-students?${src.query}`, auth());
        list = (res.data?.students || []).map((s) => toPerson(s));
      }
      setData((d) => ({ ...d, [key]: list.filter((p) => isEmail(p.email)) }));
    } catch {
      setData((d) => ({ ...d, [key]: 'error' }));
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students/recipient-groups`, auth());
      setGroups(res.data?.groups || []);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    if (source === 'saved') {
      if (groups === null) loadGroups();
    } else if (data[source] === undefined) {
      loadSource(source);
    }
  }, [source, data, groups, loadSource, loadGroups]);

  const openGroup = async (g) => {
    setGroupId(g.Id);
    const key = `group-${g.Id}`;
    setData((d) => ({ ...d, [key]: null }));
    try {
      const res = await axios.get(`${API}/api/admin/students/recipient-groups/${g.Id}`, auth());
      const list = (res.data?.recipients || [])
        .map((r) => toPerson({ id: r.StudentId || r.Email, studentName: r.StudentName, email: r.Email, lastClassDate: r.LastClassDate }))
        .filter((p) => isEmail(p.email));
      setData((d) => ({ ...d, [key]: list }));
      axios.put(`${API}/api/admin/students/recipient-groups/${g.Id}/use`, {}, auth()).catch(() => {});
    } catch {
      setData((d) => ({ ...d, [key]: 'error' }));
    }
  };

  const listKey = source === 'saved' ? (groupId ? `group-${groupId}` : null) : source;
  const raw = listKey ? data[listKey] : [];
  const loading = raw === null;
  const failed = raw === 'error';

  // everyone matching the filters
  const matching = useMemo(() => {
    if (!Array.isArray(raw)) return [];
    if (compLevel && ['allpaid', 'current', 'former', 'saved'].includes(source)) return raw.filter((p) => levelFits(compLevel, p.age));
    if (source !== 'trial') return raw;
    const since = trial.period === 'all' ? null : Date.now() - Number(trial.period) * 86400000;
    return raw.filter((p) => {
      if (trial.attended === 'yes' && !p.attended) return false;
      if (trial.attended === 'no' && p.attended) return false;
      if (!trial.results.includes(resultKey(p.result))) return false;
      if (trial.excludePaid && p.paidAccount) return false;
      if (since && (!p.demoDate || new Date(p.demoDate).getTime() < since)) return false;
      return true;
    });
  }, [raw, source, trial, compLevel]);

  const recipients = useMemo(() => matching.filter((p) => !excluded.has(p.id)), [matching, excluded]);
  const q = search.trim().toLowerCase();
  const shown = q ? matching.filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)) : matching;

  const pickSource = (key) => {
    setSource(key);
    setCompLevel(null);
    setQuick(null);
    setExcluded(new Set());
    setSearch('');
    if (key !== 'saved') setGroupId(null);
  };
  const pickQuick = (qk) => {
    const p = QUICK.find((x) => x.key === qk);
    setQuick(qk);
    setSource(p.source);
    setCompLevel(null);
    if (p.trial) setTrial(p.trial);
    setExcluded(new Set());
    setSearch('');
    const tpl = TEMPLATES.find((t) => t.key === p.template);
    if (tpl && (!dirty || window.confirm(`Use the “${tpl.name}” template? Your current text will be replaced.`))) {
      applyTemplate(p.template);
      if (p.template === 'competition' && comps?.length) applyCompetition(comps[0]); // the next / open challenge
    }
  };
  const setTrialField = (patch) => {
    setQuick(null);
    setTrial((t) => ({ ...t, ...patch }));
  };
  const toggleResult = (k) => setTrialField({ results: trial.results.includes(k) ? trial.results.filter((x) => x !== k) : [...trial.results, k] });
  const toggleOne = (id) =>
    setExcluded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const setAllShown = (on) =>
    setExcluded((s) => {
      const n = new Set(s);
      shown.forEach((p) => (on ? n.delete(p.id) : n.add(p.id)));
      return n;
    });

  /* ----- save the selection as a list ----- */
  const [saving, setSaving] = useState(null); // { name, busy, error }
  const saveList = async () => {
    if (!saving?.name?.trim()) return setSaving((s) => ({ ...s, error: 'Give the list a name.' }));
    setSaving((s) => ({ ...s, busy: true, error: '' }));
    try {
      await axios.post(
        `${API}/api/admin/students/save-recipient-group`,
        {
          groupName: saving.name.trim(),
          description: (QUICK.find((x) => x.key === quick) || SOURCES.find((s) => s.key === source))?.label || '',
          recipients: recipients.map((r) => ({ id: r.rawId, email: r.email, studentName: r.name, lastClassDate: r.lastClassDate })),
        },
        { ...auth(SENDER), timeout: 120000 }
      );
      setSaving(null);
      setGroups(null);
      say('List saved');
    } catch (err) {
      setSaving((s) => ({ ...s, busy: false, error: err.response?.data?.error || 'Couldn’t save the list.' }));
    }
    return undefined;
  };

  /* ----- message ----- */
  const [draft, setDraft] = useState(() => loadDraft() || fromTemplate('trial-next-step'));
  const [dirty, setDirty] = useState(false);
  const lastField = useRef({ name: 'body', el: null });
  useEffect(() => {
    const t = setTimeout(() => store.set(DRAFT_KEY, JSON.stringify(draft)), 400);
    return () => clearTimeout(t);
  }, [draft]);

  const setField = (name, value) => {
    setDraft((d) => ({ ...d, [name]: value }));
    setDirty(true);
  };
  function applyTemplate(key) {
    setDraft(fromTemplate(key));
    setDirty(false);
    setCompId('');
    setCompLevel(null);
  }
  const chooseTemplate = (key) => {
    const t = TEMPLATES.find((x) => x.key === key);
    if (dirty && !window.confirm(`Use the “${t.name}” template? Your current text will be replaced.`)) return;
    applyTemplate(key);
    if (key === 'competition' && comps?.length === 1) applyCompetition(comps[0]); // only one challenge: use it
  };
  const insertTag = (tag) => {
    const { name, el } = lastField.current;
    const value = String(draft[name] ?? '');
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;
    setField(name, value.slice(0, start) + tag + value.slice(end));
    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(start + tag.length, start + tag.length);
      }
    });
  };
  const track = (name) => (e) => {
    lastField.current = { name, el: e.target };
  };

  /* ----- competitions: pick a challenge, the email + audience fill in ----- */
  const [comps, setComps] = useState(null); // open / coming-soon challenges
  const [compId, setCompId] = useState('');
  const applyCompetition = (c) => {
    if (!['allpaid', 'current', 'former', 'saved'].includes(source)) pickSource('allpaid');
    setCompLevel(c.level === 'all' ? null : c.level);
    setCompId(String(c.id));
    setDraft(competitionDraft(c));
    setDirty(true);
  };
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/admin/competitions`, auth())
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.competitions || [])
          .filter((c) => c.status === 'open' || c.status === 'upcoming')
          .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
        setComps(list);
        // opened from Admin > Competitions (?competition=ID)
        const cid = new URLSearchParams(window.location.search).get('competition');
        const c = cid && (res.data?.competitions || []).find((x) => String(x.id) === String(cid));
        if (c) {
          pickSource('allpaid');
          setCompLevel(c.level === 'all' ? null : c.level);
          setCompId(String(c.id));
          setDraft(competitionDraft(c));
          setDirty(true);
        }
      })
      .catch(() => !cancelled && setComps([]));
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pickCompetition = (id) => {
    const c = (comps || []).find((x) => String(x.id) === String(id));
    if (!c) return setCompId('');
    if (dirty && draft.templateKey === 'competition' && compId && compId !== String(id) && !window.confirm(`Use “${c.title}”? Your current text will be replaced.`)) return undefined;
    applyCompetition(c);
    return undefined;
  };

  const isCenters = source === 'centers';
  const html = useMemo(() => buildEmail({ ...draft, audience: source }), [draft, source]);

  /* ----- family invite codes (referral email): loaded only when {{referralCode}} is used ----- */
  const usesReferral = useMemo(
    () => [draft.subject, draft.preheader, draft.eyebrow, draft.headline, draft.intro, draft.body, draft.highlights, draft.buttonLabel, draft.customUrl].some((t) => /\{\{referralCode\}\}/.test(t || '')),
    [draft]
  );
  const [refCodes, setRefCodes] = useState({}); // student id -> code ('' = asked, none)
  const [refLoading, setRefLoading] = useState(false);
  const [refError, setRefError] = useState(false);
  useEffect(() => {
    if (!usesReferral || isCenters) return undefined;
    const missing = recipients.map((r) => String(r.rawId)).filter((id) => !(id in refCodes));
    if (!missing.length) return undefined;
    let cancelled = false;
    setRefLoading(true);
    setRefError(false);
    axios
      .post(`${API}/api/admin/referrals/codes`, { ids: missing }, { ...auth(), timeout: 120000 })
      .then((res) => {
        if (cancelled) return;
        const got = res.data?.codes || {};
        setRefCodes((m) => {
          const n = { ...m };
          missing.forEach((id) => (n[id] = got[id] || ''));
          return n;
        });
      })
      .catch(() => !cancelled && setRefError(true))
      .finally(() => !cancelled && setRefLoading(false));
    return () => {
      cancelled = true;
    };
  }, [usesReferral, isCenters, recipients, refCodes]);
  const withCode = useCallback((p) => (p && p.kind !== 'center' ? { ...p, referralCode: refCodes[String(p.rawId)] || '' } : p), [refCodes]);
  const noCode = usesReferral && !isCenters ? recipients.filter((r) => refCodes[String(r.rawId)] === '').length : 0;

  /* ----- preview as one real person ----- */
  const [device, setDevice] = useState('desktop');
  const [sampleIdx, setSampleIdx] = useState(0);
  const sample = recipients[sampleIdx % Math.max(recipients.length, 1)] || (isCenters ? { kind: 'center', name: 'Your center' } : { kind: 'student', name: 'Karim Haddad', lastClassDate: null });
  const previewHtml = useMemo(() => fillTags(html, valuesFor(withCode(sample))), [html, sample, withCode]);
  const previewSubject = fillTags(draft.subject, valuesFor(withCode(sample)), { html: false });

  /* ----- checks ----- */
  const allText = [draft.subject, draft.preheader, draft.eyebrow, draft.headline, draft.intro, draft.body, draft.highlights, draft.buttonLabel];
  const usesChildTags = allText.some((t) => /\{\{(firstName|studentName|lastClassDate)\}\}/.test(t || ''));
  const usesCenterTag = allText.some((t) => /\{\{centerName\}\}/.test(t || ''));
  const left = leftovers(...allText);
  const problems = [
    !recipients.length && 'Nobody is selected.',
    !draft.subject?.trim() && 'Add a subject.',
    !draft.headline?.trim() && 'Add a headline.',
    left.length > 0 && `Fill in: ${left.join(', ')}`,
    isCenters && usesChildTags && 'Centers have no child: remove the child tags.',
    !isCenters && usesCenterTag && 'Parents have no center: remove {{centerName}}.',
    draft.linkType === 'custom' && !/^https?:\/\//.test(draft.customUrl || '') && 'The button link must start with https://',
    usesReferral && isCenters && 'Centers have no invite code: remove {{referralCode}}.',
    usesReferral && refLoading && 'Loading the families’ invite links…',
    usesReferral && refError && 'Couldn’t load the invite links. Reload the page and try again.',
    noCode > 0 && `${noCode} ${noCode === 1 ? 'person is' : 'people are'} not enrolled students, so they have no invite link. Untick them or use Current students.`,
  ].filter(Boolean);

  /* ----- test + send ----- */
  const [testEmail, setTestEmail] = useState(() => store.get(TEST_KEY) || '');
  const [testing, setTesting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);

  const endpoint = isCenters ? '/api/admin/students/centers/send-broadcast' : '/api/admin/students/send-broadcast-email';
  const personal = (p, body = html, subject = draft.subject) => {
    const v = valuesFor(withCode(p));
    const base = { email: p.email, personalizedSubject: fillTags(subject, v, { html: false }), personalizedBody: fillTags(body, v) };
    return p.kind === 'center' ? { ...base, centerName: p.name } : { ...base, studentName: p.name, lastClassDate: p.lastClassDate };
  };

  const sendTest = async () => {
    if (!isEmail(testEmail)) return say('Enter your email for the test.', 'bad');
    store.set(TEST_KEY, testEmail.trim());
    setTesting(true);
    try {
      const one = personal({ ...sample, email: testEmail.trim() });
      one.personalizedSubject = `[Test] ${one.personalizedSubject}`;
      const res = await axios.post(`${API}${endpoint}`, { recipients: [one], subject: `[Test] ${draft.subject}`, body: html, type: 'test' }, { ...auth(SENDER), timeout: 60000 });
      if (res.data?.totalSent) say(`Test sent to ${testEmail.trim()}`);
      else say('The test couldn’t be sent.', 'bad');
    } catch (err) {
      say(err.response?.data?.error || 'The test couldn’t be sent.', 'bad');
    } finally {
      setTesting(false);
    }
    return undefined;
  };

  const runSend = async (people, { body = html, subject = draft.subject, type = isCenters ? 'centers-broadcast' : 'broadcast', url = endpoint } = {}) => {
    const list = people.map((p) => personal(p, body, subject));
    let logId = null;
    let sent = 0;
    let bad = 0;
    setProgress({ total: list.length, done: 0, sent: 0, failed: 0, finished: false, subject: fillTags(subject, valuesFor(people[0] || {}), { html: false }) });
    const batches = packBatches(list, { subject, body, type, logId: 999999 });
    let done = 0;
    for (let b = 0; b < batches.length; b += 1) {
      const chunk = batches[b];
      const last = b === batches.length - 1;
      try {
        const res = await axios.post(`${API}${url}`, { recipients: chunk, subject, body, type, logId }, { ...auth(SENDER), timeout: 90000 });
        logId = res.data?.logId || logId;
        sent += res.data?.totalSent || 0;
        bad += res.data?.totalFailed || 0;
        done += chunk.length;
        setProgress((p) => ({ ...p, done, sent, failed: bad }));
        if (!last) await wait(BATCH_GAP);
      } catch (err) {
        bad += chunk.length;
        done += chunk.length;
        const reason = sendError(err);
        console.error('Email batch failed:', reason, err);
        setProgress((p) => ({ ...p, done, sent, failed: bad, error: reason }));
        if (!last) await wait(ERROR_GAP);
      }
    }
    setProgress((p) => ({ ...p, finished: true }));
    setCampaigns(null);
  };

  const minutes = Math.max(1, Math.ceil(((Math.ceil(recipients.length / BATCH) - 1) * BATCH_GAP + recipients.length * 1000) / 60000));

  /* ----- sent campaigns ----- */
  const [campaigns, setCampaigns] = useState(null);
  const [report, setReport] = useState(null);
  const loadCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students/email-logs`, auth());
      setCampaigns(groupCampaigns(res.data?.logs || []));
    } catch {
      setCampaigns('error');
    }
  }, []);
  useEffect(() => {
    if (view === 'sent' && campaigns === null) loadCampaigns();
  }, [view, campaigns, loadCampaigns]);

  const openReport = async (c) => {
    setReport({ campaign: c, loading: true });
    try {
      const parts = await Promise.all(c.ids.map((id) => axios.get(`${API}/api/admin/students/email-logs/${id}`, auth())));
      setReport({ campaign: c, loading: false, log: parts[0]?.data?.log, recipients: parts.flatMap((p) => p.data?.recipients || []) });
    } catch {
      setReport({ campaign: c, loading: false, error: 'Couldn’t load this report.' });
    }
  };
  const retryFailed = async () => {
    const r = report;
    const people = (r.recipients || [])
      .filter((x) => x.Status === 'Failed')
      .map((x) => (r.campaign.type === 'centers-broadcast' ? { kind: 'center', name: x.StudentName, email: x.Email } : { kind: 'student', name: x.StudentName, email: x.Email }));
    if (!people.length || !r.log?.Body) return;
    if (!window.confirm(`Send again to ${plural(people.length, 'person', 'people')} who didn’t get it?`)) return;
    setReport(null);
    const isC = r.campaign.type === 'centers-broadcast';
    await runSend(people, { body: r.log.Body, subject: r.log.Subject, type: r.campaign.type, url: isC ? '/api/admin/students/centers/send-broadcast' : '/api/admin/students/send-broadcast-email' });
  };

  const tags = isCenters ? TAGS.center : TAGS.student;
  const templates = TEMPLATES.filter((t) => t.fits.includes(source));
  const counts = useMemo(() => {
    if (source !== 'trial' || !Array.isArray(raw)) return null;
    const c = {};
    raw.forEach((p) => {
      const k = resultKey(p.result);
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [raw, source]);

  /* =========================================================
     Render
     ========================================================= */
  return (
    <div className="rmes">
      <header className="rmes-head">
        <div>
          <span className="rmes-eyebrow">Email Studio</span>
          <h1>Send emails</h1>
        </div>
        <div className="rmes-viewtabs" role="tablist">
          <button type="button" role="tab" aria-selected={view === 'compose'} className={view === 'compose' ? 'is-on' : ''} onClick={() => setView('compose')}>
            <FontAwesomeIcon icon={faPenNib} /> New email
          </button>
          <button type="button" role="tab" aria-selected={view === 'sent'} className={view === 'sent' ? 'is-on' : ''} onClick={() => setView('sent')}>
            <FontAwesomeIcon icon={faClockRotateLeft} /> Sent
          </button>
        </div>
      </header>

      {view === 'compose' ? (
        <>
          {/* quick picks */}
          <div className="rmes-quick" aria-label="Quick picks">
            {QUICK.map((p) => (
              <button key={p.key} type="button" className={`rmes-quickbtn ${quick === p.key ? 'is-on' : ''}`} onClick={() => pickQuick(p.key)}>
                <FontAwesomeIcon icon={p.icon} /> {p.label}
              </button>
            ))}
          </div>

          <div className="rmes-grid">
            {/* ================= 1. Audience ================= */}
            <section className="rmes-panel rmes-aud">
              <h2 className="rmes-h2">
                <span className="rmes-num">1</span> Who gets it
              </h2>

              <div className="rmes-sources" role="radiogroup" aria-label="Audience">
                {SOURCES.map((s) => (
                  <button key={s.key} type="button" role="radio" aria-checked={source === s.key} className={`rmes-source ${source === s.key ? 'is-on' : ''}`} onClick={() => pickSource(s.key)}>
                    <FontAwesomeIcon icon={s.icon} />
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>

              {source === 'trial' && (
                <div className="rmes-filters">
                  <div className="rmes-filter">
                    <span className="rmes-flabel">Free class</span>
                    <div className="rmes-seg">
                      {[
                        ['yes', 'Attended'],
                        ['no', 'Missed'],
                        ['any', 'Any'],
                      ].map(([k, l]) => (
                        <button key={k} type="button" className={trial.attended === k ? 'is-on' : ''} onClick={() => setTrialField({ attended: k })}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="rmes-filter">
                    <span className="rmes-flabel">Status after the demo</span>
                    <div className="rmes-chips">
                      {RESULTS.map((r) => (
                        <button key={r.key} type="button" className={`rmes-chip ${trial.results.includes(r.key) ? 'is-on' : ''} ${r.key === 'enrolled' ? 'is-enrolled' : ''}`} onClick={() => toggleResult(r.key)} aria-pressed={trial.results.includes(r.key)}>
                          {r.label}
                          {counts && <em>{counts[r.key] || 0}</em>}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="rmes-filter rmes-filter--row">
                    <label className="rmes-check">
                      <input type="checkbox" checked={trial.excludePaid} onChange={(e) => setTrialField({ excludePaid: e.target.checked })} />
                      Skip families who already have a paid account
                    </label>
                    <select className="rmes-select" value={trial.period} onChange={(e) => setTrialField({ period: e.target.value })} aria-label="Demo date">
                      {PERIODS.map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {(source === 'allpaid' || (compLevel && ['current', 'former', 'saved'].includes(source))) && (
                <p className="rmes-note">
                  {source === 'allpaid' && 'Every paid student, including paused and stopped ones (one email per parent).'}
                  {compLevel && (
                    <>
                      {' '}
                      Only {compLevel === 'little' ? 'children aged 6 to 8' : 'children aged 9 and up'}, for this challenge.{' '}
                      <button type="button" className="rmes-link" onClick={() => setCompLevel(null)}>
                        Show everyone
                      </button>
                    </>
                  )}
                </p>
              )}

              {source === 'centers' && (
                <p className="rmes-note">
                  To add or edit centers, use the <Link to="/Admin/sendemails-classic?tab=centers">centers list</Link>.
                </p>
              )}

              {source === 'saved' && (
                <div className="rmes-groups">
                  {groups === null ? (
                    <p className="rmes-muted">Loading lists…</p>
                  ) : groups.length === 0 ? (
                    <p className="rmes-muted">No saved lists yet.</p>
                  ) : (
                    groups.map((g) => (
                      <button key={g.Id} type="button" className={`rmes-group ${groupId === g.Id ? 'is-on' : ''}`} onClick={() => openGroup(g)}>
                        <strong>{g.GroupName}</strong>
                        <span>{plural(Number(g.RecipientCount || 0), 'person', 'people')}</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    ))
                  )}
                </div>
              )}

              {(source !== 'saved' || groupId) && (
                <div className="rmes-people">
                  <div className="rmes-peoplehead">
                    <strong className="rmes-count">{loading ? '…' : recipients.length}</strong>
                    <span>
                      {isCenters ? 'centers' : 'families'} selected
                      {matching.length !== recipients.length && <em> of {matching.length}</em>}
                    </span>
                    {recipients.length > 0 && !isCenters && (
                      <button type="button" className="rmes-link" onClick={() => setSaving({ name: '', busy: false, error: '' })}>
                        <FontAwesomeIcon icon={faFloppyDisk} /> Save list
                      </button>
                    )}
                  </div>
                  <div className="rmes-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input type="search" placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  {shown.length > 0 && (
                    <div className="rmes-bulk">
                      <button type="button" onClick={() => setAllShown(true)}>
                        Select all
                      </button>
                      <button type="button" onClick={() => setAllShown(false)}>
                        Select none
                      </button>
                    </div>
                  )}
                  <ul className="rmes-list">
                    {loading && <li className="rmes-muted">Loading…</li>}
                    {failed && (
                      <li className="rmes-bad">
                        Couldn’t load this audience.{' '}
                        <button type="button" className="rmes-link" onClick={() => (source === 'saved' ? openGroup({ Id: groupId }) : loadSource(source))}>
                          Try again
                        </button>
                      </li>
                    )}
                    {!loading && !failed && shown.length === 0 && <li className="rmes-muted">Nobody matches these filters.</li>}
                    {shown.slice(0, 400).map((p) => (
                      <li key={p.id}>
                        <label className={`rmes-person ${excluded.has(p.id) ? 'is-out' : ''}`}>
                          <input type="checkbox" checked={!excluded.has(p.id)} onChange={() => toggleOne(p.id)} />
                          <span className="rmes-pname">
                            <strong>{p.name}</strong>
                            <small>{p.email}</small>
                          </span>
                          {p.kind === 'student' && source === 'trial' && (
                            <span className="rmes-ptags">
                              <span className={`rmes-tag ${p.attended ? 'is-good' : 'is-warn'}`}>{p.attended ? 'Attended' : 'Missed'}</span>
                              <span className="rmes-tag">{resultLabel(p.result)}</span>
                              {p.demoDate && <small>{dateLabel(p.demoDate)}</small>}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                    {shown.length > 400 && <li className="rmes-muted">+ {shown.length - 400} more (all included). Use search to find someone.</li>}
                  </ul>
                </div>
              )}
            </section>

            {/* ================= 2. Message ================= */}
            <section className="rmes-panel rmes-msg">
              <h2 className="rmes-h2">
                <span className="rmes-num">2</span> Write it
              </h2>

              <div className="rmes-templates">
                <span className="rmes-flabel">
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> Start from
                </span>
                <div className="rmes-tplrow">
                  {templates.map((t) => (
                    <button key={t.key} type="button" className={`rmes-tpl ${draft.templateKey === t.key ? 'is-on' : ''}`} onClick={() => chooseTemplate(t.key)}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              {draft.templateKey === 'competition' && (
                <label className="rmes-compick">
                  <span className="rmes-flabel">
                    <FontAwesomeIcon icon={faTrophy} /> Challenge
                  </span>
                  {comps === null ? (
                    <small className="rmes-muted">Loading…</small>
                  ) : comps.length === 0 ? (
                    <small className="rmes-muted">
                      No open or coming challenge. <Link to="/Admin/Competitions">Create one</Link>
                    </small>
                  ) : (
                    <select className="rmes-select" value={compId} onChange={(e) => pickCompetition(e.target.value)}>
                      <option value="">Pick a challenge…</option>
                      {comps.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title} · {c.status === 'open' ? `open until ${compDay(c.endDate)}` : `starts ${compDay(c.startDate)}`}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              )}

              <div className="rmes-tags">
                <span className="rmes-flabel">Personal tags</span>
                {tags.map(([tag, label]) => (
                  <button key={tag} type="button" className="rmes-tagbtn" onMouseDown={(e) => e.preventDefault()} onClick={() => insertTag(tag)} title={`Insert ${tag}`}>
                    + {label}
                  </button>
                ))}
              </div>

              <Field label="Subject" hint={previewSubject && `Looks like: ${previewSubject}`}>
                <input className="rmes-input" value={draft.subject} onChange={(e) => setField('subject', e.target.value)} onFocus={track('subject')} onSelect={track('subject')} maxLength={150} />
              </Field>
              <Field label="Inbox preview line" hint="The grey text shown after the subject in the inbox.">
                <input className="rmes-input" value={draft.preheader} onChange={(e) => setField('preheader', e.target.value)} onFocus={track('preheader')} onSelect={track('preheader')} maxLength={150} />
              </Field>

              <div className="rmes-block">
                <span className="rmes-blocklabel">Top banner</span>
                <div className="rmes-themes" role="radiogroup" aria-label="Color">
                  {Object.entries(THEMES).map(([k, t]) => (
                    <button key={k} type="button" role="radio" aria-checked={draft.theme === k} title={t.label} className={`rmes-theme ${draft.theme === k ? 'is-on' : ''}`} style={{ background: t.hero }} onClick={() => setField('theme', k)}>
                      <span className="rmes-sr">{t.label}</span>
                    </button>
                  ))}
                </div>
                <Field label="Small title">
                  <input className="rmes-input" value={draft.eyebrow} onChange={(e) => setField('eyebrow', e.target.value)} onFocus={track('eyebrow')} onSelect={track('eyebrow')} maxLength={60} />
                </Field>
                <Field label="Headline">
                  <input className="rmes-input rmes-input--big" value={draft.headline} onChange={(e) => setField('headline', e.target.value)} onFocus={track('headline')} onSelect={track('headline')} maxLength={120} />
                </Field>
                <Field label="Short intro">
                  <textarea className="rmes-input" rows={2} value={draft.intro} onChange={(e) => setField('intro', e.target.value)} onFocus={track('intro')} onSelect={track('intro')} />
                </Field>
              </div>

              <div className="rmes-block">
                <span className="rmes-blocklabel">Message</span>
                <textarea className="rmes-input rmes-body" rows={8} value={draft.body} onChange={(e) => setField('body', e.target.value)} onFocus={track('body')} onSelect={track('body')} />
                <p className="rmes-help">Empty line = new paragraph · “- ” at the start = list · **bold** · links work as-is</p>
                <Field label="Highlights (one per line, up to 4)" hint="Shown as a box with check marks. Leave empty to hide.">
                  <textarea className="rmes-input" rows={3} value={draft.highlights} onChange={(e) => setField('highlights', e.target.value)} onFocus={track('highlights')} onSelect={track('highlights')} />
                </Field>
              </div>

              <div className="rmes-block">
                <span className="rmes-blocklabel">Button</span>
                <div className="rmes-two">
                  <Field label="Goes to">
                    <select className="rmes-input" value={draft.linkType} onChange={(e) => setField('linkType', e.target.value)}>
                      {Object.entries(LINKS).map(([k, l]) => (
                        <option key={k} value={k}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {draft.linkType !== 'none' && (
                    <Field label="Button text">
                      <input className="rmes-input" value={draft.buttonLabel} onChange={(e) => setField('buttonLabel', e.target.value)} onFocus={track('buttonLabel')} onSelect={track('buttonLabel')} maxLength={40} />
                    </Field>
                  )}
                </div>
                {draft.linkType === 'custom' && (
                  <Field label="Link">
                    <input className="rmes-input" type="url" placeholder="https://" value={draft.customUrl} onChange={(e) => setField('customUrl', e.target.value)} />
                  </Field>
                )}
              </div>

              <div className="rmes-block">
                <span className="rmes-blocklabel">Sign-off</span>
                <div className="rmes-two">
                  <Field label="Closing">
                    <input className="rmes-input" value={draft.signOff} onChange={(e) => setField('signOff', e.target.value)} maxLength={40} />
                  </Field>
                  <Field label="From">
                    <input className="rmes-input" value={draft.signature} onChange={(e) => setField('signature', e.target.value)} maxLength={60} />
                  </Field>
                </div>
              </div>
            </section>

            {/* ================= 3. Preview ================= */}
            <section className="rmes-panel rmes-prev">
              <div className="rmes-prevhead">
                <h2 className="rmes-h2">
                  <span className="rmes-num">3</span> Preview
                </h2>
                <div className="rmes-seg rmes-seg--icons">
                  <button type="button" className={device === 'desktop' ? 'is-on' : ''} onClick={() => setDevice('desktop')} aria-label="Desktop">
                    <FontAwesomeIcon icon={faDesktop} />
                  </button>
                  <button type="button" className={device === 'phone' ? 'is-on' : ''} onClick={() => setDevice('phone')} aria-label="Phone">
                    <FontAwesomeIcon icon={faMobileScreen} />
                  </button>
                </div>
              </div>
              <div className="rmes-inbox">
                <span className="rmes-from">RoboMind Academy</span>
                <strong>{previewSubject || '(no subject)'}</strong>
                <span className="rmes-pre">{fillTags(draft.preheader, valuesFor(sample), { html: false })}</span>
              </div>
              <div className="rmes-as">
                As <strong>{sample.name}</strong>
                {recipients.length > 1 && (
                  <button type="button" className="rmes-link" onClick={() => setSampleIdx((i) => i + 1)}>
                    <FontAwesomeIcon icon={faRotateRight} /> Next person
                  </button>
                )}
              </div>
              <div className={`rmes-frame is-${device}`}>
                <iframe title="Email preview" srcDoc={previewHtml} sandbox="" />
              </div>
            </section>
          </div>

          {/* ================= send bar ================= */}
          <div className="rmes-bar">
            <div className="rmes-barinfo">
              {problems.length ? (
                <span className="rmes-bad">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> {problems[0]}
                  {problems.length > 1 && <em> (+{problems.length - 1})</em>}
                </span>
              ) : (
                <span className="rmes-good">
                  <FontAwesomeIcon icon={faCircleCheck} /> Ready for {plural(recipients.length, isCenters ? 'center' : 'family', isCenters ? 'centers' : 'families')}
                </span>
              )}
            </div>
            <div className="rmes-test">
              <input type="email" placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} aria-label="Test email address" />
              <button type="button" className="rmes-btn rmes-btn--ghost" onClick={sendTest} disabled={testing || !draft.subject?.trim()}>
                <FontAwesomeIcon icon={faFlask} /> {testing ? 'Sending…' : 'Send test'}
              </button>
            </div>
            <button type="button" className="rmes-btn rmes-btn--main" disabled={problems.length > 0 || !!progress} onClick={() => setConfirmOpen(true)}>
              <FontAwesomeIcon icon={faPaperPlane} /> Send to {recipients.length || 0}
            </button>
          </div>
        </>
      ) : (
        /* ================= Sent ================= */
        <section className="rmes-panel rmes-sent">
          {campaigns === null ? (
            <p className="rmes-muted">Loading…</p>
          ) : campaigns === 'error' ? (
            <p className="rmes-bad">
              Couldn’t load the sent emails.{' '}
              <button type="button" className="rmes-link" onClick={() => setCampaigns(null)}>
                Try again
              </button>
            </p>
          ) : campaigns.length === 0 ? (
            <p className="rmes-muted">Nothing sent yet.</p>
          ) : (
            <ul className="rmes-camps">
              {campaigns.map((c) => {
                const pct = c.total ? Math.round((c.sent / c.total) * 100) : 0;
                return (
                  <li key={c.key}>
                    <button type="button" className="rmes-camp" onClick={() => openReport(c)}>
                      <span className="rmes-campmain">
                        <strong>{c.subject}</strong>
                        <small>
                          {sentAt(c.at)} · {c.type === 'centers-broadcast' ? 'Centers' : 'Parents'}
                        </small>
                      </span>
                      <span className="rmes-campnums">
                        <b>{c.sent}</b>/{c.total} sent
                        {c.failed > 0 && <em className="rmes-badtext"> · {c.failed} failed</em>}
                      </span>
                      <span className="rmes-meter" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ---------- confirm ---------- */}
      {confirmOpen && (
        <div className="rmes-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="rmes-modal" role="dialog" aria-modal="true" aria-label="Send" onClick={(e) => e.stopPropagation()}>
            <h3>Send to {plural(recipients.length, isCenters ? 'center' : 'family', isCenters ? 'centers' : 'families')}?</h3>
            <p>
              “{draft.subject}” · about {plural(minutes, 'minute')}. Keep this page open until it finishes.
            </p>
            <div className="rmes-modalfoot">
              <button type="button" className="rmes-btn rmes-btn--ghost" onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="rmes-btn rmes-btn--main"
                onClick={() => {
                  setConfirmOpen(false);
                  runSend(recipients);
                }}
              >
                <FontAwesomeIcon icon={faPaperPlane} /> Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- progress ---------- */}
      {progress && (
        <div className="rmes-overlay">
          <div className="rmes-modal" role="dialog" aria-modal="true" aria-label="Sending">
            <h3>{progress.finished ? 'Done' : 'Sending…'}</h3>
            <p className="rmes-muted">{progress.subject}</p>
            <div className="rmes-progress" aria-hidden="true">
              <span style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
            </div>
            <p>
              <b>{progress.sent}</b> sent{progress.failed > 0 && <span className="rmes-badtext"> · {progress.failed} failed</span>} · {progress.done}/{progress.total}
            </p>
            {progress.error && <p className="rmes-badtext">Why some failed: {progress.error}</p>}
            {!progress.finished && <p className="rmes-muted">Keep this page open.</p>}
            {progress.finished && (
              <div className="rmes-modalfoot">
                <button
                  type="button"
                  className="rmes-btn rmes-btn--ghost"
                  onClick={() => {
                    setProgress(null);
                    setView('sent');
                  }}
                >
                  See report
                </button>
                <button type="button" className="rmes-btn rmes-btn--main" onClick={() => setProgress(null)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- report ---------- */}
      {report && (
        <div className="rmes-overlay" onClick={() => setReport(null)}>
          <div className="rmes-modal rmes-modal--wide" role="dialog" aria-modal="true" aria-label="Report" onClick={(e) => e.stopPropagation()}>
            <div className="rmes-modaltop">
              <h3>{report.campaign.subject}</h3>
              <button type="button" className="rmes-x" onClick={() => setReport(null)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {report.loading ? (
              <p className="rmes-muted">Loading…</p>
            ) : report.error ? (
              <p className="rmes-bad">{report.error}</p>
            ) : (
              <>
                <p>
                  {sentAt(report.campaign.at)} · <b>{report.campaign.sent}</b> sent
                  {report.campaign.failed > 0 && <span className="rmes-badtext"> · {report.campaign.failed} failed</span>}
                </p>
                <ul className="rmes-replist">
                  {(report.recipients || []).map((r, i) => (
                    <li key={`${r.Email}-${i}`} className={r.Status === 'Failed' ? 'is-failed' : ''}>
                      <span>
                        <strong>{r.StudentName || r.Email}</strong>
                        <small>{r.Email}</small>
                      </span>
                      <span className={`rmes-tag ${r.Status === 'Failed' ? 'is-bad' : 'is-good'}`} title={r.ErrorMessage || ''}>
                        {r.Status}
                      </span>
                    </li>
                  ))}
                </ul>
                {(report.recipients || []).some((r) => r.Status === 'Failed') && (
                  <div className="rmes-modalfoot">
                    <button type="button" className="rmes-btn rmes-btn--main" onClick={retryFailed}>
                      <FontAwesomeIcon icon={faRotateRight} /> Send again to failed
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- save list ---------- */}
      {saving && (
        <div className="rmes-overlay" onClick={() => !saving.busy && setSaving(null)}>
          <div className="rmes-modal" role="dialog" aria-modal="true" aria-label="Save list" onClick={(e) => e.stopPropagation()}>
            <h3>Save these {recipients.length} as a list</h3>
            <input className="rmes-input" autoFocus placeholder="List name" value={saving.name} onChange={(e) => setSaving((s) => ({ ...s, name: e.target.value, error: '' }))} />
            {saving.error && <p className="rmes-bad">{saving.error}</p>}
            <div className="rmes-modalfoot">
              <button type="button" className="rmes-btn rmes-btn--ghost" onClick={() => setSaving(null)} disabled={saving.busy}>
                Cancel
              </button>
              <button type="button" className="rmes-btn rmes-btn--main" onClick={saveList} disabled={saving.busy}>
                {saving.busy ? 'Saving…' : 'Save list'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div key={toast.id} className={`rmes-toast is-${toast.tone}`} role="status">
          {toast.text}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="rmes-field">
    <span className="rmes-flabel">{label}</span>
    {children}
    {hint && <small className="rmes-hint">{hint}</small>}
  </label>
);

export default EmailStudio;
